import { Chart, Plugin, TooltipItem } from "chart.js";

import { themeProps } from "../../../theme";
import { IExternalTooltipBodyLine } from "../external-tooltip";

type TChartType = "line";

interface ICursorLabelTooltipOptions {
  unitsX?: string;
  unitsY?: string;
  xOffset?: number;
  yOffset?: number;
  xValueFormatter?: (value: number, dataPoint: TooltipItem<TChartType>) => string | null | undefined;
  yValueFormatter?: (value: number, dataPoint: TooltipItem<TChartType>) => string | null | undefined;
}

interface IHarmonicCursorsOptions {
  enabled?: boolean;
  maxHarmonics?: number;
  lineColor?: string;
  xOffset?: number;
}

interface ISubHarmonicCursorsOptions {
  enabled?: boolean;
  maxDividers?: number;
  lineColor?: string;
  xOffset?: number;
}

type ChartWithCrosshair = Chart<TChartType> & {
  crosshair?: {
    enabled: boolean;
    x: number | null;
    baseValue?: number | null;
    baseDatasetIndex?: number | null;
  };
  _cursorLabelHoverKey?: string | null;
};

type IDataPoint = { x: number; y: number };

interface ICursorFrequency {
  frequency: number;
  lineColor: string;
}

interface ICursorDatasetPoint {
  label: string;
  xValue: number;
  yValue: number;
  color: string;
  datasetIndex: number;
}

interface ICursorLabelEntry {
  anchorX: number;
  anchorY: number;
  datasets: ICursorDatasetPoint[];
  unitsX?: string;
  unitsY?: string;
  xValueFormatter?: ICursorLabelTooltipOptions["xValueFormatter"];
  yValueFormatter?: ICursorLabelTooltipOptions["yValueFormatter"];
}

const CONTAINER_CLASS = "chart-cursor-labels";
const TITLE_FONT_SIZE = "11px";
const BODY_FONT_SIZE = "10px";
const COLOR_DOT_SIZE = "7px";
const LABEL_BASE_Z_INDEX = 1;
const LABEL_HOVER_Z_INDEX = 100;

const formatTooltipNumber = (value: number) => value.toFixed(2);

const appendUnits = (value: string, units?: string) => {
  if (!units) {
    return value;
  }

  const separatorIndex = value.indexOf(";");

  if (separatorIndex === -1) {
    return `${value} ${units}`;
  }

  const primaryValue = value.slice(0, separatorIndex).trimEnd();
  const suffix = value.slice(separatorIndex);

  return `${primaryValue} ${units}${suffix}`;
};

const formatTooltipValue = (
  value: number,
  dataPoint: TooltipItem<TChartType>,
  formatter?: (value: number, dataPoint: TooltipItem<TChartType>) => string | null | undefined,
) => {
  const formattedValue = formatter?.(value, dataPoint);

  if (typeof formattedValue === "string" && formattedValue !== "") {
    return formattedValue;
  }

  return formatTooltipNumber(value);
};

const findClosestPointAtX = (data: IDataPoint[], expectedX: number): IDataPoint | null => {
  let closestIdx = -1;
  let minDiff = Number.POSITIVE_INFINITY;

  data.forEach((pt, idx) => {
    if (pt && typeof pt.x === "number") {
      const diff = Math.abs(pt.x - expectedX);
      if (diff < minDiff) {
        minDiff = diff;
        closestIdx = idx;
      }
    }
  });

  return closestIdx === -1 ? null : data[closestIdx];
};

const getTooltipOptions = (chart: Chart<TChartType>): ICursorLabelTooltipOptions =>
  (chart.config.options?.plugins?.tooltip as ICursorLabelTooltipOptions | undefined) ?? {};

const collectCursorFrequencies = (
  baseFreq: number,
  min: number,
  max: number,
  harmonicOpts?: IHarmonicCursorsOptions,
  subHarmonicOpts?: ISubHarmonicCursorsOptions,
): ICursorFrequency[] => {
  const result: ICursorFrequency[] = [];

  if (harmonicOpts?.enabled) {
    const maxHarmonics = harmonicOpts.maxHarmonics ?? 5;
    const lineColor = harmonicOpts.lineColor ?? "rgb(253, 0, 0)";

    for (let k = 2; k <= maxHarmonics; k++) {
      const fk = baseFreq * k;
      if (fk > max) break;
      if (fk < min) continue;
      result.push({ frequency: fk, lineColor });
    }
  }

  if (subHarmonicOpts?.enabled) {
    const maxDividers = subHarmonicOpts.maxDividers ?? 5;
    const lineColor = subHarmonicOpts.lineColor ?? "rgb(0, 0, 255)";

    for (let n = 2; n <= maxDividers; n++) {
      const fn = baseFreq / n;
      if (!Number.isFinite(fn) || fn <= 0) continue;
      if (fn < min || fn > max) continue;
      result.push({ frequency: fn, lineColor });
    }
  }

  return result;
};

const buildCursorLabelEntries = (chart: ChartWithCrosshair, cursors: ICursorFrequency[]): ICursorLabelEntry[] => {
  const tooltipOptions = getTooltipOptions(chart);
  const { unitsX, unitsY, xOffset = 0, yOffset = 0, xValueFormatter, yValueFormatter } = tooltipOptions;

  const harmonicOpts = (chart.options.plugins as { harmonicCursors?: IHarmonicCursorsOptions })?.harmonicCursors;
  const subHarmonicOpts = (chart.options.plugins as { subHarmonicCursors?: ISubHarmonicCursorsOptions })
    ?.subHarmonicCursors;
  const pluginXOffset = harmonicOpts?.xOffset ?? subHarmonicOpts?.xOffset ?? xOffset;

  const xScale = chart.scales.x;
  const meta0 = chart.getDatasetMeta(0) as { yAxisID?: string } | undefined;
  const yAxisId = meta0?.yAxisID;
  const yScale = yAxisId ? chart.scales[yAxisId] : chart.scales.y;

  if (!xScale || !yScale || typeof xScale.getPixelForValue !== "function") {
    return [];
  }

  const datasets = chart.data.datasets;
  const is3D = datasets.length > 1 && pluginXOffset !== 0;
  const activeDatasetIndex = chart.crosshair?.baseDatasetIndex;
  const datasetIndexes =
    typeof activeDatasetIndex === "number" && activeDatasetIndex >= 0 && activeDatasetIndex < datasets.length
      ? [activeDatasetIndex]
      : datasets.map((_, index) => index);

  return cursors.flatMap(({ frequency }) => {
    const datasetPoints: ICursorDatasetPoint[] = [];

    datasetIndexes.forEach(dsIndex => {
      const meta = chart.getDatasetMeta(dsIndex);
      if (meta?.hidden) {
        return;
      }

      const ds = datasets[dsIndex];
      if (!ds) {
        return;
      }

      const expectedX = is3D ? frequency + dsIndex * pluginXOffset : frequency;
      const pt = findClosestPointAtX(ds.data as IDataPoint[], expectedX);

      if (!pt || typeof pt.x !== "number" || typeof pt.y !== "number") {
        return;
      }

      datasetPoints.push({
        label: ds.label ?? "",
        xValue: pt.x - dsIndex * pluginXOffset,
        yValue: pt.y - dsIndex * yOffset,
        color: (ds.borderColor as string) ?? "",
        datasetIndex: dsIndex,
      });
    });

    if (!datasetPoints.length) {
      return [];
    }

    const anchorDatasetIndex = datasetPoints[0].datasetIndex;
    const anchorDataset = datasets[anchorDatasetIndex];
    const anchorExpectedX = is3D ? frequency + anchorDatasetIndex * pluginXOffset : frequency;
    const renderedPoint = anchorDataset
      ? findClosestPointAtX(anchorDataset.data as IDataPoint[], anchorExpectedX)
      : null;

    const anchorX = renderedPoint ? xScale.getPixelForValue(renderedPoint.x) : xScale.getPixelForValue(frequency);
    const anchorY = renderedPoint ? yScale.getPixelForValue(renderedPoint.y) : yScale.getPixelForValue(yScale.min);

    return [
      {
        anchorX,
        anchorY,
        datasets: datasetPoints,
        unitsX,
        unitsY,
        xValueFormatter,
        yValueFormatter,
      },
    ];
  });
};

const buildLabelContent = (chart: Chart<TChartType>, entry: ICursorLabelEntry) => {
  const titleLines = entry.datasets.map(({ label, xValue, datasetIndex }) => {
    const dataPoint = { datasetIndex, dataset: chart.data.datasets[datasetIndex] } as TooltipItem<TChartType>;
    return `${label}: ${appendUnits(formatTooltipValue(xValue, dataPoint, entry.xValueFormatter), entry.unitsX)}`;
  });

  const bodyLines: IExternalTooltipBodyLine[] = entry.datasets.map(({ yValue, color, datasetIndex }) => {
    const dataPoint = { datasetIndex, dataset: chart.data.datasets[datasetIndex] } as TooltipItem<TChartType>;
    return {
      text: appendUnits(formatTooltipValue(yValue, dataPoint, entry.yValueFormatter), entry.unitsY),
      color,
    };
  });

  return { titleLines, bodyLines };
};

const getOrCreateLabelsContainer = (chart: Chart<TChartType>): HTMLDivElement | null => {
  const parent = chart.canvas.parentNode;

  if (!parent || !(parent instanceof HTMLElement)) {
    return null;
  }

  let container = parent.querySelector<HTMLDivElement>(`.${CONTAINER_CLASS}`);

  if (!container) {
    container = document.createElement("div");
    container.className = CONTAINER_CLASS;
    container.style.position = "absolute";
    container.style.top = "0";
    container.style.left = "0";
    container.style.width = "100%";
    container.style.height = "100%";
    container.style.pointerEvents = "none";
    container.style.zIndex = "999";
    container.style.overflow = "hidden";
    parent.appendChild(container);
  }

  if (typeof themeProps.options?.typography === "object") {
    container.style.fontFamily = themeProps.options.typography.fontFamily ?? "";
  }

  return container;
};

const positionLabelElement = (
  labelEl: HTMLDivElement,
  chart: Chart<TChartType>,
  anchorX: number,
  anchorY: number,
  container: HTMLDivElement,
) => {
  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
  const cursorX = positionX + anchorX;
  const cursorY = positionY + anchorY;

  labelEl.style.visibility = "hidden";
  labelEl.style.left = "0";
  labelEl.style.top = "0";
  container.appendChild(labelEl);

  const labelRect = labelEl.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const containerWidth = containerRect.width;
  const containerHeight = containerRect.height;

  let horizontalPosition: number;

  if (cursorX - labelRect.width / 2 < 0) {
    horizontalPosition = 0;
  } else if (cursorX + labelRect.width / 2 > containerWidth) {
    horizontalPosition = containerWidth - labelRect.width;
  } else {
    horizontalPosition = cursorX - labelRect.width / 2;
  }

  const verticalBelow = cursorY + 10;
  const verticalAbove = cursorY - labelRect.height - 10;
  const verticalPosition =
    verticalBelow + labelRect.height > containerHeight && verticalAbove >= 0 ? verticalAbove : verticalBelow;

  Object.assign(labelEl.style, {
    visibility: "visible",
    left: `${horizontalPosition}px`,
    top: `${verticalPosition}px`,
  });
};

const createLabelElement = (chart: Chart<TChartType>, entry: ICursorLabelEntry) => {
  const { titleLines, bodyLines } = buildLabelContent(chart, entry);

  const labelEl = document.createElement("div");
  labelEl.style.background = "rgba(0, 0, 0, 0.7)";
  labelEl.style.borderRadius = "3px";
  labelEl.style.color = "white";
  labelEl.style.position = "absolute";
  labelEl.style.pointerEvents = "auto";
  labelEl.style.cursor = "default";
  labelEl.style.padding = "3px 6px";
  labelEl.style.whiteSpace = "nowrap";

  const table = document.createElement("table");
  table.style.margin = "0";
  table.style.borderCollapse = "collapse";

  const tableHead = document.createElement("thead");
  titleLines.forEach(title => {
    const tr = document.createElement("tr");
    const th = document.createElement("th");
    th.style.borderWidth = "0";
    th.style.fontSize = TITLE_FONT_SIZE;
    th.style.fontWeight = "normal";
    th.style.textAlign = "left";
    th.style.padding = "0";
    th.appendChild(document.createTextNode(title));
    tr.appendChild(th);
    tableHead.appendChild(tr);
  });

  const tableBody = document.createElement("tbody");
  bodyLines.forEach(body => {
    const tr = document.createElement("tr");
    const td = document.createElement("td");
    td.style.borderWidth = "0";
    td.style.fontSize = BODY_FONT_SIZE;
    td.style.padding = "0";
    td.style.textAlign = "left";

    if (body.color) {
      const span = document.createElement("span");
      span.style.background = body.color;
      span.style.borderColor = body.color;
      span.style.borderRadius = "50px";
      span.style.borderWidth = "2px";
      span.style.marginRight = "6px";
      span.style.height = COLOR_DOT_SIZE;
      span.style.width = COLOR_DOT_SIZE;
      span.style.display = "inline-block";
      span.style.verticalAlign = "middle";
      td.appendChild(span);
    }

    td.appendChild(document.createTextNode(body.text));
    tr.appendChild(td);
    tableBody.appendChild(tr);
  });

  table.appendChild(tableHead);
  table.appendChild(tableBody);
  labelEl.appendChild(table);

  return labelEl;
};

const getCursorLabelHoverKey = (entry: ICursorLabelEntry) => `${entry.anchorX}`;

const attachLabelHoverBehavior = (
  labelEl: HTMLDivElement,
  chart: ChartWithCrosshair,
  hoverKey: string,
  baseZIndex: number,
) => {
  labelEl.style.zIndex = String(chart._cursorLabelHoverKey === hoverKey ? LABEL_HOVER_Z_INDEX : baseZIndex);

  labelEl.addEventListener("mouseenter", () => {
    chart._cursorLabelHoverKey = hoverKey;
    labelEl.style.zIndex = String(LABEL_HOVER_Z_INDEX);
  });

  labelEl.addEventListener("mouseleave", () => {
    if (chart._cursorLabelHoverKey === hoverKey) {
      chart._cursorLabelHoverKey = null;
    }
    labelEl.style.zIndex = String(baseZIndex);
  });
};

const renderCursorLabels = (chart: ChartWithCrosshair, entries: ICursorLabelEntry[]) => {
  const container = getOrCreateLabelsContainer(chart);

  if (!container) {
    return;
  }

  container.replaceChildren();

  if (!entries.length) {
    container.style.display = "none";
    return;
  }

  container.style.display = "block";

  entries.forEach((entry, index) => {
    const labelEl = createLabelElement(chart, entry);
    const hoverKey = getCursorLabelHoverKey(entry);
    const baseZIndex = LABEL_BASE_Z_INDEX + index;

    attachLabelHoverBehavior(labelEl, chart, hoverKey, baseZIndex);
    positionLabelElement(labelEl, chart, entry.anchorX, entry.anchorY, container);
  });
};

const hideCursorLabels = (chart: Pick<Chart, "canvas">) => {
  const parent = chart.canvas.parentNode;

  if (!parent || !(parent instanceof HTMLElement)) {
    return;
  }

  const container = parent.querySelector<HTMLDivElement>(`.${CONTAINER_CLASS}`);
  if (container) {
    container.replaceChildren();
    container.style.display = "none";
  }
};

export const AdditionalCursorLabelsPlugin: Plugin<TChartType> = {
  id: "additionalCursorLabels",

  afterDraw(chartInstance) {
    const chart = chartInstance as ChartWithCrosshair;
    const plugins = chart.options.plugins ?? {};
    const harmonicOpts = (plugins as { harmonicCursors?: IHarmonicCursorsOptions }).harmonicCursors;
    const subHarmonicOpts = (plugins as { subHarmonicCursors?: ISubHarmonicCursorsOptions }).subHarmonicCursors;

    const harmonicEnabled = harmonicOpts?.enabled === true;
    const subHarmonicEnabled = subHarmonicOpts?.enabled === true;

    if (!harmonicEnabled && !subHarmonicEnabled) {
      hideCursorLabels(chart);
      return;
    }

    if (!chart.crosshair?.enabled || chart.crosshair.x == null) {
      hideCursorLabels(chart);
      return;
    }

    const xScale = chart.scales.x;
    if (!xScale || typeof xScale.getValueForPixel !== "function") {
      hideCursorLabels(chart);
      return;
    }

    const baseFromState = chart.crosshair.baseValue;
    const baseFreq =
      typeof baseFromState === "number" && Number.isFinite(baseFromState) && baseFromState > 0
        ? baseFromState
        : xScale.getValueForPixel(chart.crosshair.x);

    if (typeof baseFreq !== "number" || !Number.isFinite(baseFreq) || baseFreq <= 0) {
      hideCursorLabels(chart);
      return;
    }

    const min = xScale.min;
    const max = xScale.max;

    if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) {
      hideCursorLabels(chart);
      return;
    }

    const cursors = collectCursorFrequencies(baseFreq, min, max, harmonicOpts, subHarmonicOpts);

    if (!cursors.length) {
      hideCursorLabels(chart);
      return;
    }

    const entries = buildCursorLabelEntries(chart, cursors);
    renderCursorLabels(chart, entries);
  },

  beforeDestroy(chartInstance) {
    hideCursorLabels(chartInstance);
    const parent = chartInstance.canvas?.parentNode;

    if (parent && parent instanceof HTMLElement) {
      parent.querySelector(`.${CONTAINER_CLASS}`)?.remove();
    }
  },
};

export default AdditionalCursorLabelsPlugin;
